// LeadDigitalTwinPanel — Phase 6.
//
// Fetches /api/leads/digital-twin. Renders the synthesized profile:
// personality type, comm style preference, best contact windows,
// buying readiness, likely objections, product propensity, LTV,
// churn risk, referral potential, and a "how to approach" directive.

import React from 'react';
import { readPersistedJwtDirect } from './authedFetch';
import { supabase } from '../lib/supabase';

async function bearer() {
  const token = readPersistedJwtDirect();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const PERSONALITY_STYLE = {
  analytical: { icon: '📊', bg: '#e0e7ff', border: '#a5b4fc', text: '#3730a3' },
  driver:     { icon: '⚡',  bg: '#fee2e2', border: '#fca5a5', text: '#991b1b' },
  amiable:    { icon: '🤝',  bg: '#dcfce7', border: '#86efac', text: '#166534' },
  expressive: { icon: '🎭', bg: '#fef3c7', border: '#fcd34d', text: '#92400e' },
};

const PRODUCT_LABEL = {
  aca_ppo: 'ACA PPO', aca_hmo: 'ACA HMO', private_ppo: 'Private PPO',
  accident: 'Accident', dental_vision: 'Dental+Vision', critical_illness: 'Critical illness',
  hospital_indemnity: 'Hospital indemnity', life_term: 'Term life', life_iul: 'IUL',
  medicare_advantage: 'MA', medicare_supp: 'Med Supp', medicare_pdp: 'PDP',
};

function Meter({ score, tone }) {
  return (
    <div style={{ height: 6, background: '#e2e8f0', borderRadius: 999, overflow: 'hidden' }}>
      <div style={{ width: `${score}%`, height: '100%', background: tone, transition: 'width 240ms' }}/>
    </div>
  );
}

export default function LeadDigitalTwinPanel({ lead, callHistory = [], outreachLog = [] }) {
  const [state, setState] = React.useState({ loading: true, data: null, error: null });

  React.useEffect(() => {
    const controller = new AbortController();
    (async () => {
      const { authedFetchLead } = await import('./authedFetch');
      const res = await authedFetchLead('/api/leads/digital-twin', { lead, callHistory, outreachLog }, controller.signal);
      if (controller.signal.aborted) return;
      setState({ loading: false, data: res.ok ? res.data : null, error: res.ok ? null : res.error });
    })();
    return () => { controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.id, callHistory.length, outreachLog.length]);

  if (state.loading) return null;
  if (state.error) {
    return (
      <div style={{
        background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 10,
        padding: 10, fontSize: 11, color: '#92400e', marginBottom: 8,
      }}>
        Digital Twin temporarily unavailable ({String(state.error)}).
      </div>
    );
  }
  if (!state.data) return null;
  const d = state.data;
  const ps = PERSONALITY_STYLE[d.personality_type] || PERSONALITY_STYLE.analytical;

  return (
    <div style={{
      background: 'linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)',
      border: '1px solid #6366f1',
      borderRadius: 12,
      padding: 14,
      color: '#e0e7ff',
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
        <div style={{ fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#a5b4fc', fontWeight: 800 }}>
          Digital Twin
        </div>
        <div style={{ fontSize: 11, color: '#c7d2fe' }}>Synthesized profile</div>
      </div>

      {/* Personality + how to approach */}
      <div style={{
        display: 'flex', gap: 12, alignItems: 'flex-start',
        background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 8, padding: 10, marginBottom: 10,
      }}>
        <div style={{
          background: ps.bg, color: ps.text, border: `1px solid ${ps.border}`,
          borderRadius: 8, padding: '6px 10px', fontSize: 12, fontWeight: 800,
          textTransform: 'capitalize',
        }}>
          {ps.icon} {d.personality_type}
        </div>
        <div style={{ flex: 1, minWidth: 0, fontSize: 12, color: '#e0e7ff', lineHeight: 1.55 }}>
          {d.how_to_approach}
        </div>
      </div>

      {/* Scores row */}
      <div style={{
        display: 'grid', gap: 10,
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
        marginBottom: 10,
      }}>
        <ScoreCell label="Buying readiness" score={d.buying_readiness_score} tone="#22c55e"/>
        <ScoreCell label="Churn risk"       score={d.churn_risk}             tone="#ef4444" invert/>
        <ScoreCell label="Referral potential" score={d.referral_potential}    tone="#a855f7"/>
        <div>
          <div style={{ fontSize: 9, color: '#a5b4fc', letterSpacing: '0.15em', textTransform: 'uppercase', fontWeight: 700 }}>3yr LTV</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#22c55e', fontFamily: 'JetBrains Mono, monospace', marginTop: 3 }}>
            ${d.ltv_dollars.toLocaleString()}
          </div>
        </div>
      </div>

      {/* Best contact windows */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 9, color: '#a5b4fc', letterSpacing: '0.15em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 }}>
          Best contact windows · {d.comm_style_preference === 'brief' ? 'Prefers brief' : 'Prefers detail'}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {d.best_contact_windows.map(w => (
            <div key={w.label} style={{ flex: 1, textAlign: 'center' }}>
              <div style={{
                background: `rgba(165,180,252,${0.15 + w.weight / 300})`,
                border: '1px solid rgba(165,180,252,0.3)',
                borderRadius: 6, padding: '6px 4px',
              }}>
                <div style={{ fontSize: 11, color: '#e0e7ff', fontWeight: 700 }}>{w.label}</div>
                <div style={{ fontSize: 10, color: '#c7d2fe', marginTop: 2, fontFamily: 'JetBrains Mono, monospace' }}>{w.weight}%</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Product propensity */}
      {d.product_propensity.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 9, color: '#a5b4fc', letterSpacing: '0.15em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 }}>
            Product propensity
          </div>
          <div style={{ display: 'grid', gap: 4 }}>
            {d.product_propensity.map(p => (
              <div key={p.product} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ fontSize: 11, color: '#e0e7ff', minWidth: 130 }}>{PRODUCT_LABEL[p.product] || p.product}</div>
                <div style={{ flex: 1 }}>
                  <Meter score={p.score} tone={p.score >= 70 ? '#22c55e' : p.score >= 50 ? '#eab308' : '#94a3b8'}/>
                </div>
                <div style={{ fontSize: 10, color: '#c7d2fe', fontFamily: 'JetBrains Mono, monospace', minWidth: 24, textAlign: 'right' }}>{p.score}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Likely objections */}
      <div>
        <div style={{ fontSize: 9, color: '#a5b4fc', letterSpacing: '0.15em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 }}>
          Likely objections
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {d.likely_objections.map(o => (
            <span key={o} style={{
              fontSize: 10, background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.15)',
              color: '#c7d2fe', padding: '3px 8px', borderRadius: 999,
            }}>{o}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

function ScoreCell({ label, score, tone, invert }) {
  const displayTone = invert ? (score >= 70 ? '#ef4444' : score >= 40 ? '#eab308' : '#22c55e') : tone;
  return (
    <div>
      <div style={{ fontSize: 9, color: '#a5b4fc', letterSpacing: '0.15em', textTransform: 'uppercase', fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: displayTone, fontFamily: 'JetBrains Mono, monospace', margin: '3px 0' }}>
        {score}
      </div>
      <Meter score={score} tone={displayTone}/>
    </div>
  );
}
